import { useSyncExternalStore } from 'react';
import { prefersReducedMotion, usePrefs } from '@/lib/prefs';

/**
 * Motion presets for the `motion` package (ux.md section 2) plus a reduced-motion hook.
 *
 *   <motion.div animate={{ scale: 1 }} transition={springs.pop} />
 *   const reduced = useReducedMotion(); if (reduced) skip the choreography.
 *
 * The objects are plain data, so they also work for hand-rolled rAF code.
 */

export const springs = {
  /** Buttons, toggles. */
  snappy: { type: 'spring', stiffness: 520, damping: 34, mass: 0.8 },
  /** Badges, score bumps, a slight overshoot. */
  pop: { type: 'spring', stiffness: 380, damping: 18, mass: 1 },
  /** Sheets, page pushes. */
  sheet: { type: 'spring', stiffness: 300, damping: 32, mass: 1 },
  /** Card flip in the reveal. */
  heavy: { type: 'spring', stiffness: 160, damping: 20, mass: 1.4 },
} as const;

/** Milliseconds, mirroring the --dur-* vars in theme.css. */
export const durations = { instant: 80, fast: 150, base: 240, slow: 400, reveal: 1200 } as const;

/** List stagger and attribute-bar stagger, in seconds for `motion`. */
export const stagger = { list: 0.05, bars: 0.07 } as const;

/** Cubic-bezier control points, mirroring --ease-snap and --ease-out-expo. */
export const easings = {
  snap: [0.2, 0.9, 0.1, 1],
  outExpo: [0.16, 1, 0.3, 1],
} as const;

/** easeOutExpo for rAF loops: t in 0..1. */
export function easeOutExpo(t: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return 1 - 2 ** (-10 * t);
}

/** A crossfade under 150 ms, the stand-in for any transform under reduced motion. */
export const reducedFade = { duration: 0.12, ease: 'linear' } as const;

function subscribe(cb: () => void): () => void {
  const unsubPrefs = usePrefs.subscribe(cb);
  const mq = typeof window !== 'undefined' ? window.matchMedia?.('(prefers-reduced-motion: reduce)') : undefined;
  mq?.addEventListener?.('change', cb);
  return () => {
    unsubPrefs();
    mq?.removeEventListener?.('change', cb);
  };
}

/** True when the OS asks for less motion and the in-app toggle does not override it. */
export function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribe, prefersReducedMotion, () => false);
}
